<script>
  import { getContext } from 'svelte';

  // DOCUMENTATION:
  // To hide elements from the drag image, add the class 'drag-image-ignore' to the element you want to hide.
  // for example <button class="drag-handle drag-image-ignore">...</button>

  let {
    children,
    /** @type {number} */
    index,
    /** @type {string} */
    align='items-center',
    is_editing,
    class: css_class,
    ...rest
  } = $props()

  let { 
		set_dragging_item, 
		update_list_order, 
		/** @type {'row'|'column'} */
		direction,    
	} = getContext('draggable_context');


	/** @type {boolean} */
	let is_drop_over_start = $state(false);
	/** @type {boolean} */
	let is_drop_over_end = $state(false);
	/** @type {boolean} */
	let highlight_selection = $state(false);
	/** @type {boolean} */
	let is_dragging = $state(false);

	
  function on_drag_start(event) {
		if (!is_editing) return;

		const draggableElement = event.currentTarget;
		if (!draggableElement) return;

		event.dataTransfer.effectAllowed = 'move'; // allowed actions: 'none', 'copy', 'copyLink', 'copyMove', 'link', 'linkMove', 'move', 'all',
    event.dataTransfer.dropEffect = 'move'; // allowed effects:  'copy', 'move', 'link', or 'none'
    set_dragging_item(index);
    event.dataTransfer.setData('text/plain', index.toString());

		is_dragging = true;

		// Offset the drag image to match the cursor position
		const rect = draggableElement.getBoundingClientRect();
		const x = event.clientX - rect.left;
		const y = event.clientY - rect.top;

		event.dataTransfer.setDragImage(draggableElement, x, y);

		draggableElement.addEventListener('dragend', () => {
			is_dragging = false;
		}, { once: true });
	}

	function on_drop(event) {
	  event.preventDefault();
	  const start_index = parseInt(event.dataTransfer.getData('text/plain'));

		// Scenario 1: Dropping on itself - no action needed
	  if (start_index === index) {
	    return reset();
	  }
	
	  let drop_index;
	
	  // Determine drop index based on drop position
	  if (is_drop_over_start) {
	    drop_index = index; // Intend to place before the current element
	  } else if (is_drop_over_end) {
	    drop_index = index + 1; // Intend to place after the current element
	  }
	
	  // Scenario 2: Special case - dropping the first element onto the top half of the second element, it should stay in place
	  if (start_index === 0 && drop_index === 1 && is_drop_over_start) {
		   return reset();
	  }
	
	  // todo: check if within the bounds of the list
		update_list_order(start_index, drop_index);

		// visual hint: indicate where the item was dropped
		highlight_selection = true;
		setTimeout(() => {
			highlight_selection = false;
		}, 300);

		reset()
	}

	function reset() {
		// Reset the drop indicators
	  is_drop_over_start = false;
	  is_drop_over_end = false;
    is_dragging = false;
	}

  function on_drag_over(event) {
    event.preventDefault();
    const targetRect = event.currentTarget.getBoundingClientRect();
    let over_second_half;

    if (direction === 'column') {
      const middle_y = (targetRect.bottom - targetRect.top) / 2;
      over_second_half = event.clientY - targetRect.top > middle_y;
    } else { // 'row'
      const middle_x = (targetRect.right - targetRect.left) / 2;
      over_second_half = event.clientX - targetRect.left > middle_x;
    }

    is_drop_over_start = !over_second_half;
    is_drop_over_end = over_second_half;
  }

  function on_drag_leave(event) {
    is_drop_over_start = false;
    is_drop_over_end = false;
  }

  function on_key_down(event) {
    const currentElement = event.currentTarget.closest('.draggable-item');
    let targetElement = null;

    if (event.key === 'ArrowDown') {
      if (currentElement.nextElementSibling) { // Check if there is a next sibling
        targetElement = currentElement.nextElementSibling;
        while (targetElement && !targetElement.classList.contains('draggable-item')) {
          targetElement = targetElement.nextElementSibling;
        }
        if (targetElement) { // Move only if not the last element
          update_list_order(index, index + 1);
          focusTargetElement(targetElement);
        }
      }
    } else if (event.key === 'ArrowUp') {
      if (currentElement.previousElementSibling) { // Check if there is a previous sibling
        targetElement = currentElement.previousElementSibling;
        while (targetElement && !targetElement.classList.contains('draggable-item')) {
          targetElement = targetElement.previousElementSibling;
        }
        if (targetElement) { // Move only if not the first element
          update_list_order(index, index - 1);
          focusTargetElement(targetElement);
        }
      }
    }

    // Prevent default if a target element was found and focused
    if (targetElement) {
      event.preventDefault();
    }
  }

  function focusTargetElement(targetElement) {
    const focusable = targetElement.querySelector('[tabindex="0"]');
    if (focusable) {
      focusable.focus();
    }
  }
</script>

{#if children}
<!-- svelte-ignore a11y_click_events_have_key_events -->
<!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
<!-- svelte-ignore a11y_no_static_element_interactions -->
  <div
    role="listitem"
    class="draggable-item flex-row gap-x g-2 {align} {direction} {is_drop_over_start ? 'drag-over-start' : ''} {is_drop_over_end ? 'drag-over-end' : ''} {css_class}"
    class:selected-fill={highlight_selection}
    class:is-dragging={is_dragging === true}
    draggable={is_editing}
    ondragstart={is_editing ? on_drag_start : null}
    ondrop={is_editing ? on_drop : null}
    ondragover={is_editing ? on_drag_over : null}
    ondragleave={is_editing ? on_drag_leave : null}
    onkeydown={is_editing ? on_key_down : null}
    {...rest}
    >
    {@render children()}
  </div>
{/if}

<style lang="postcss">
  :root {
    --draggable-indicator-thickness: 4px;
  }

  .draggable-item {
    position: relative;
    /* 
      Important:
      This element should not have any padding, margin, or border.
      Otherwise the placement of the indicators is not correct anymore (needs to be offset by margin, border, padding)
     */
    transform: translate(0, 0);
    will-change: transform;
  }
	.draggable-item, .draggable-item:active {
		cursor: pointer;
	}
  .draggable-item:not([draggable="true"]) {
    cursor: default;
  }
  /* Indicators for column layout */
  .column.drag-over-start:before, .column.drag-over-end:after {
		content: "";
		position: absolute;
		background-color: var(--editing);
		left: 0;
		right: 0;
		height: var(--draggable-indicator-thickness);
		z-index: 2;
		pointer-events: none;
  }

  .column.drag-over-start:before {
    top: 0;
		transform: translateY(-50%);
  }

  .column.drag-over-end:after {
    bottom: 0;
		transform: translateY(50%);
  }
		
  /* Indicators for row layout */
  .row.drag-over-start:before, .row.drag-over-end:after {
    content: "";
    position: absolute;
    background-color: var(--editing);
    top: 0;
    bottom: 0;
    width: var(--draggable-indicator-thickness);
    z-index: 2;
  }

  .row.drag-over-start:before {
    left: 0;
		transform: translateX(-50%);
  }

  .row.drag-over-end:after {
    right: 0;
		transform: translateX(50%);
  }

  .draggable-item.is-dragging :global(.drag-image-ignore) {
    visibility: hidden;
  }

  /* When dragging, ensure the element is visually isolated */
  .draggable-item.is-dragging {
    transform: translate(0, 0);
    position: relative;
    z-index: 1;
    background-color: var(--color-default-background-50);
    box-shadow: var(--selected-fill), var(--selected-border);
    :global(*) {
      opacity: 0.9;
    }
    :global([popover]) {
      /* Bug in Safari: 
        when dragging an element with a popover, all the elements that follow the dragged item become part of the drag image,
        even if the popover has visibility: hidden.
        This is a workaround to hide the popover using "display: none" during dragging.
      */
      display: none;
    }
  }
</style>
